import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Info, Wrench, X } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

interface BroadcastMessage {
  id: string;
  message: string;
  type: "warning" | "info" | "maintenance";
  isActive: boolean;
  createdAt: string;
}

export function BroadcastPopup() {
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(true);

  const { data: broadcast } = useQuery<BroadcastMessage | null>({
    queryKey: ["/api/broadcast"],
    refetchInterval: 60000,
  });

  useEffect(() => {
    const storedDismissed = localStorage.getItem("broadcast_dismissed");
    if (storedDismissed) {
      setDismissed(storedDismissed);
    }
  }, []);

  useEffect(() => {
    if (broadcast && broadcast.id !== dismissed) {
      setIsOpen(true);
    }
  }, [broadcast, dismissed]);

  if (!broadcast || !broadcast.isActive || dismissed === broadcast.id) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem("broadcast_dismissed", broadcast.id);
    setDismissed(broadcast.id);
    setIsOpen(false);
  };

  const getIcon = () => {
    switch (broadcast.type) {
      case "warning":
        return <AlertTriangle className="w-6 h-6 text-amber-500" />;
      case "maintenance":
        return <Wrench className="w-6 h-6 text-blue-500" />;
      default:
        return <Info className="w-6 h-6 text-blue-400" />;
    }
  };

  const getTitle = () => {
    switch (broadcast.type) {
      case "warning":
        return "Important Notice";
      case "maintenance":
        return "Scheduled Maintenance";
      default:
        return "Information";
    }
  };

  const getBgColor = () => {
    switch (broadcast.type) {
      case "warning":
        return "bg-amber-500/10 border-amber-500/30";
      case "maintenance":
        return "bg-blue-500/10 border-blue-500/30";
      default:
        return "bg-blue-400/10 border-blue-400/30";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md" 
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            {getIcon()}
            <DialogTitle className="text-lg">{getTitle()}</DialogTitle>
          </div>
        </DialogHeader>
        <DialogDescription asChild>
          <div className={`p-4 rounded-lg border ${getBgColor()}`}>
            <p className="text-foreground text-sm leading-relaxed" data-testid="text-broadcast-message">{broadcast.message}</p>
          </div>
        </DialogDescription>
        <div className="flex justify-end mt-2">
          <Button 
            variant="default" 
            onClick={handleDismiss}
            data-testid="button-dismiss-broadcast"
          >
            <X className="w-4 h-4 mr-1" /> Dismiss
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
